import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";

import { getCategories } from "../../core/category.core";
import { useTranslation } from "../../core/translation.core";
import ChevronRightIcon from "../../icons/chevronRight.icon";
import CloseIcon from "../../icons/iconClose.comp";
import { CatSlugItem, Category } from "../../types/category";
import Skeleton from "../common/skeleton.comp";
import LayoutGridIcon from "../../icons/posicons/layoutGrid.icon";

export function CategoryBtns({
  route,
  showOverlay,
  setShowOverlay,
}: {
  route: "/" | "/products";
  showOverlay: boolean;
  setShowOverlay: (showOverlay: boolean) => void;
}) {
  const { t, msg } = useTranslation();
  const [activeCat, setActiveCat] = useState<any[]>([]);
  const [categoryCrumbs, setCategoryCrumbs] = useState<CatSlugItem[]>([] as CatSlugItem[]);
  const { push } = useRouter();

  const handleToggleShow = (closeModal: boolean, resetModal: boolean) => {
    setShowOverlay(closeModal);
    //  @ts-ignore
    resetModal && setActiveCat(data);
    if (resetModal) {
      push(route);
      setCategoryCrumbs([]);
    }
  };

  const { data, isFetched } = useQuery({
    queryFn: async () => {
      const data = await getCategories();
      setActiveCat(data);
      return data;
    },
    queryKey: ["category"],
  });

  const handleActiveCat = (cat: CatSlugItem) => {
    //  @ts-ignore
    if (cat.children.length > 0) {
      //  @ts-ignore
      setActiveCat(cat.children);
    }
    const previousId = categoryCrumbs[categoryCrumbs.length - 1]?.id;
    if (previousId != cat.id) {
      //  @ts-ignore
      const previousChild = categoryCrumbs[categoryCrumbs.length - 1]?.children.find((i) => i.id == cat.id);
      if (previousChild) setCategoryCrumbs((pre) => [...pre, cat]);
      else {
        setCategoryCrumbs((pre) => [...pre.filter((item, key, payload) => key != payload.length - 1 && item), cat]);
      }
    }
  };

  const reverseBreadcrumbs = (id: string) => {
    let node = categoryCrumbs.find((obj) => obj.id === id);
    //  @ts-ignore
    const depth = categoryCrumbs.indexOf(node);
    if (!node) {
      return null;
    }

    //  @ts-ignore
    setActiveCat(node.children);
    setCategoryCrumbs((pre) => pre.slice(0, depth + 1));
  };

  if (!isFetched) return <Skeleton />;

  return (
    <nav className="relative w-full">
      <section className="flex justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleToggleShow(!showOverlay, false)}
            className="bg-white dark:bg-her-darkblue2 dark:text-white py-2 px-3 rounded-lg flex-center gap-3 w-fit"
          >
            <LayoutGridIcon className="text-her-mysticMaroon w-6 h-6" />
            <p>{t(msg.pos.home)}</p>
          </button>

          <BreadCrumbs reverseBreadcrumbs={reverseBreadcrumbs} items={categoryCrumbs} trackOverlay={showOverlay} />
        </div>
        {categoryCrumbs.length > 0 && (
          <button onClick={() => handleToggleShow(showOverlay, true)} className="dark:text-white text-black">
            <CloseIcon />
          </button>
        )}
      </section>

      <section className={`w-full mt-3 z-10 space-y-2 shadow-lg ${showOverlay ? "absolute" : "hidden"}`}>
        <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-5">
          {activeCat?.map((cat) => (
            <div className="md:text-sm text-xs h-[90px]" onClick={() => handleActiveCat(cat)} key={cat.slug}>
              <CatListItem route={route} cat={cat} handleToggleShow={handleToggleShow} />
            </div>
          ))}
        </div>
      </section>
    </nav>
  );
}

function CatListItem({
  route,
  cat,
  handleToggleShow,
}: {
  route: string;
  cat: Category;
  handleToggleShow: (close: boolean, resetModal: boolean) => void;
}) {
  if (cat.children?.length > 0) {
    return (
      <button className="bg-white dark:bg-her-darkblue2 dark:text-white w-full h-full px-4 py-3 rounded-md hover:bg-her-blue transition-all duration-300 flex items-center justify-between">
        <h2>{cat.name}</h2>
        <ChevronRightIcon className="w-4 h-4" />
      </button>
    );
  }

  return (
    <Link
      href={{ pathname: route, query: { page: 1, slug: cat.slug } }}
      onClick={() => handleToggleShow(false, false)}
      className="hover:bg-her-blue transition-all duration-300 bg-white dark:bg-her-darkblue2 dark:text-white px-4 py-3 w-full h-full rounded-md grid place-items-center"
    >
      {cat?.name}
    </Link>
  );
}

function BreadCrumbs({
  items,
  reverseBreadcrumbs,
  trackOverlay,
}: {
  items: CatSlugItem[];
  reverseBreadcrumbs: (id: string) => void;
  trackOverlay: boolean;
}) {
  return (
    <>
      {items.map((item, index) => {
        const isLastChild = index + 1 == items.length; // Check if it the last element
        //  @ts-ignore
        const fadeLastChild = isLastChild && item.children.length == 0; // Check if the last element is empty
        const fadeLastChildOnOpen = fadeLastChild && trackOverlay;
        return (
          <div
            key={Math.random()}
            onClick={index + 1 != items.length ? () => reverseBreadcrumbs(item.id) : () => {}}
            className={`dark:text-white flex items-center gap-1 ${index + 1 != items.length ? "cursor-pointer" : ""}`}
          >
            {!fadeLastChildOnOpen && <span className={`${fadeLastChild ? "font-light text-gray-400" : ""}`}>{item.name}</span>}
            {index + 1 != items.length && <ChevronRightIcon className="w-4 h-4" />}
          </div>
        );
      })}
    </>
  );
}
